import React, { useState } from 'react';
import { Award, ExternalLink, CheckCircle, Star, AlertCircle, Search, Filter, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { filterScholarships } from '../mock';

const US_STATES = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut",
  "Delaware", "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa",
  "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan",
  "Minnesota", "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire",
  "New Jersey", "New Mexico", "New York", "North Carolina", "North Dakota", "Ohio",
  "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota",
  "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington", "West Virginia",
  "Wisconsin", "Wyoming"
];

// Calculate match score for each scholarship
const calculateMatchScore = (scholarship, userGpa, userSat, userIncome) => {
  let score = 0;
  let factors = [];
  
  // GPA match (higher score if user exceeds requirement significantly)
  const gpaDiff = userGpa - scholarship.g;
  if (gpaDiff >= 0.5) {
    score += 30;
    factors.push("Strong GPA match");
  } else if (gpaDiff >= 0.2) {
    score += 20;
    factors.push("Good GPA match");
  } else if (gpaDiff >= 0) {
    score += 10;
    factors.push("Meets GPA");
  }
  
  // SAT match (if required)
  if (scholarship.sat > 0 && userSat > 0) {
    const satDiff = userSat - scholarship.sat;
    if (satDiff >= 100) {
      score += 25;
      factors.push("Exceeds SAT");
    } else if (satDiff >= 0) {
      score += 15;
      factors.push("Meets SAT");
    }
  } else if (scholarship.testOptional) {
    score += 20;
    factors.push("No SAT required");
  }
  
  // Income match (need-based get bonus if eligible)
  if (scholarship.maxIncome && userIncome <= scholarship.maxIncome) {
    score += 20;
    factors.push("Income eligible");
    if (userIncome <= scholarship.maxIncome * 0.7) {
      score += 10;
      factors.push("Strong need match");
    }
  } else if (!scholarship.maxIncome) {
    score += 10;
    factors.push("No income limit");
  }
  
  // Award amount bonus
  if (scholarship.a >= 10000) {
    score += 15;
  } else if (scholarship.a >= 5000) {
    score += 10;
  } else if (scholarship.a >= 2000) {
    score += 5;
  }
  
  // State-specific bonus (less competition)
  if (scholarship.state !== "any") {
    score += 15;
    factors.push("State-specific (less competition)");
  }
  
  return { score: Math.min(100, score), factors };
};

const ScholarshipCard = ({ scholarship, userGpa, userSat, userIncome, rank }) => {
  const matchData = calculateMatchScore(scholarship, userGpa, userSat, userIncome);
  
  const getMatchColor = (score) => {
    if (score >= 70) return { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-200' };
    if (score >= 50) return { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200' };
    return { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-200' };
  };
  
  const colors = getMatchColor(matchData.score);
  
  return (
    <Card className="bg-white border-gray-200 shadow-sm p-6 hover:shadow-md transition-shadow">
      <div className="flex justify-between items-start gap-4 flex-wrap">
        <div className="flex-1 min-w-[280px]">
          <div className="flex items-start gap-3 mb-3">
            {rank <= 3 && (
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${rank === 1 ? 'bg-yellow-100 text-yellow-600' : rank === 2 ? 'bg-gray-100 text-gray-600' : 'bg-orange-100 text-orange-600'}`}>
                <Star className="w-4 h-4" />
              </div>
            )}
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{scholarship.name}</h3>
              {scholarship.state !== "any" ? (
                <p className="text-sm text-blue-600 font-medium">{scholarship.state} Residents</p>
              ) : (
                <p className="text-sm text-green-600 font-medium">National Scholarship</p>
              )}
            </div>
          </div>
          
          {/* Match Score Badge */}
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-4 ${colors.bg} ${colors.border} border`}>
            <TrendingUp className={`w-4 h-4 ${colors.text}`} />
            <span className={`text-sm font-medium ${colors.text}`}>{matchData.score}% Match</span>
          </div>
          
          {/* Key Details */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="p-3 bg-green-50 rounded-lg">
              <p className="text-xs text-gray-500">Award Amount</p>
              <p className="text-xl font-bold text-green-600">${scholarship.a.toLocaleString()}</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500">Min GPA</p>
              <p className="text-xl font-bold text-gray-900">{scholarship.g}</p>
            </div>
            {scholarship.sat > 0 && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500">Min SAT</p>
                <p className="text-lg font-bold text-gray-900">{scholarship.sat}</p>
              </div>
            )}
            {scholarship.maxIncome && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500">Max Income</p>
                <p className="text-lg font-bold text-gray-900">${(scholarship.maxIncome / 1000).toFixed(0)}k</p>
              </div>
            )}
          </div>
          
          {/* Match Factors */}
          <div className="flex flex-wrap gap-2 mb-4">
            {matchData.factors.slice(0, 3).map((factor, i) => (
              <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                <CheckCircle className="w-3 h-3" />
                {factor}
              </span>
            ))}
          </div>
          
          <a 
            href={scholarship.u} 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-lg transition-colors font-medium"
          >
            Apply Now
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </div>
    </Card>
  );
};

const Scholarships = () => {
  const [formData, setFormData] = useState({
    gpa: '',
    satScore: '',
    state: '',
    income: '',
    testOptional: false
  });

  const [matches, setMatches] = useState([]);
  const [searched, setSearched] = useState(false);
  const [sortBy, setSortBy] = useState('match');

  const handleSearch = (e) => {
    e.preventDefault();
    let results = filterScholarships(
      Number(formData.gpa),
      Number(formData.satScore) || 0,
      formData.state,
      Number(formData.income),
      formData.testOptional
    );
    
    // Add match scores and sort
    results = results.map(s => ({
      ...s,
      matchData: calculateMatchScore(s, Number(formData.gpa), Number(formData.satScore) || 0, Number(formData.income))
    }));
    
    // Sort by match score by default
    results.sort((a, b) => b.matchData.score - a.matchData.score);
    
    setMatches(results);
    setSearched(true);
  };

  const handleSort = (method) => {
    setSortBy(method);
    let sorted = [...matches];
    if (method === 'match') {
      sorted.sort((a, b) => b.matchData.score - a.matchData.score);
    } else if (method === 'amount') {
      sorted.sort((a, b) => b.a - a.a);
    } else if (method === 'gpa') {
      sorted.sort((a, b) => a.g - b.g);
    }
    setMatches(sorted);
  };

  const totalPotential = matches.reduce((sum, s) => sum + s.a, 0);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-6 py-12">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-10">
            <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Award className="w-7 h-7 text-blue-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Scholarship Matcher</h1>
            <p className="text-gray-600 max-w-xl mx-auto">
              Find scholarships matched to your profile. We prioritize state and local scholarships with less competition.
            </p>
          </div>

          {/* Notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-8">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-gray-700 text-sm">
                <strong className="text-blue-700">Important:</strong> All links go to official scholarship websites. 
                Always verify eligibility and deadlines directly with the scholarship provider before applying.
              </p>
            </div>
          </div>

          {/* Search Form */}
          <Card className="bg-white border-gray-200 shadow-sm p-6 mb-8">
            <form onSubmit={handleSearch} className="space-y-6">
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="space-y-2">
                  <Label className="text-gray-700 text-sm">GPA (4.0 scale)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    max="4.0"
                    placeholder="3.5"
                    value={formData.gpa}
                    onChange={(e) => setFormData({...formData, gpa: e.target.value})}
                    className="bg-white border-gray-300 text-gray-900 h-11"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-700 text-sm">SAT Score</Label>
                  <Input
                    type="number"
                    min="400"
                    max="1600"
                    placeholder={formData.testOptional ? "Not required" : "1200"}
                    value={formData.satScore}
                    onChange={(e) => setFormData({...formData, satScore: e.target.value})}
                    className="bg-white border-gray-300 text-gray-900 h-11"
                    disabled={formData.testOptional}
                  />
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="testOptional"
                      checked={formData.testOptional}
                      onCheckedChange={(checked) => setFormData({...formData, testOptional: checked, satScore: ''})}
                      className="border-gray-300"
                    />
                    <label htmlFor="testOptional" className="text-sm text-gray-500 cursor-pointer">
                      Test Optional (No SAT)
                    </label>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-700 text-sm">Your State</Label>
                  <Select value={formData.state} onValueChange={(value) => setFormData({...formData, state: value})}>
                    <SelectTrigger className="bg-white border-gray-300 text-gray-900 h-11">
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-200 max-h-[300px]">
                      {US_STATES.map(state => (
                        <SelectItem key={state} value={state} className="text-gray-900">{state}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-700 text-sm">Family Income ($)</Label>
                  <Input
                    type="number"
                    placeholder="75000"
                    value={formData.income}
                    onChange={(e) => setFormData({...formData, income: e.target.value})}
                    className="bg-white border-gray-300 text-gray-900 h-11"
                    required
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg font-semibold"
              >
                <Search className="w-5 h-5 mr-2" />
                Find Matching Scholarships
              </Button>
            </form>
          </Card>

          {/* Results */}
          {searched && (
            <div>
              {matches.length > 0 ? (
                <>
                  {/* Results Header */}
                  <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">
                        {matches.length} Scholarships Found
                      </h2>
                      <p className="text-gray-500 text-sm">
                        Potential total: <span className="text-green-600 font-semibold">${totalPotential.toLocaleString()}</span>
                      </p>
                    </div>
                    
                    {/* Sort Options */}
                    <div className="flex items-center gap-2">
                      <Filter className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-500">Sort by:</span>
                      <div className="flex gap-1">
                        {[
                          { id: 'match', label: 'Best Match' },
                          { id: 'amount', label: 'Highest Amount' },
                          { id: 'gpa', label: 'Lowest GPA Req' }
                        ].map(opt => (
                          <Button
                            key={opt.id}
                            variant={sortBy === opt.id ? 'default' : 'ghost'}
                            size="sm"
                            onClick={() => handleSort(opt.id)}
                            className={sortBy === opt.id ? 'bg-blue-600 text-white' : 'text-gray-600'}
                          >
                            {opt.label}
                          </Button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Top Picks */}
                  {matches.slice(0, 3).some(m => m.matchData.score >= 60) && (
                    <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
                      <h3 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
                        <Star className="w-4 h-4" /> Top Picks For You
                      </h3>
                      <p className="text-green-700 text-sm">
                        Based on your profile, we recommend focusing on: {' '}
                        <strong>{matches.filter(m => m.matchData.score >= 60).slice(0, 3).map(m => m.name).join(', ')}</strong>
                      </p>
                    </div>
                  )}

                  {/* Scholarship Cards */}
                  <div className="space-y-4">
                    {matches.map((scholarship, index) => (
                      <ScholarshipCard 
                        key={index} 
                        scholarship={scholarship} 
                        userGpa={Number(formData.gpa)}
                        userSat={Number(formData.satScore) || 0}
                        userIncome={Number(formData.income)}
                        rank={index + 1}
                      />
                    ))}
                  </div>

                  {/* Tips */}
                  <Card className="bg-blue-50 border-blue-200 p-6 mt-8">
                    <h3 className="font-semibold text-gray-900 mb-3">Scholarship Application Tips</h3>
                    <ul className="space-y-2 text-sm text-gray-700">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                        Apply to multiple scholarships—even small amounts add up
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                        State and local scholarships often have less competition than national ones
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                        Keep track of deadlines—many scholarships have early fall deadlines
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                        Reuse and adapt essays for similar scholarship applications
                      </li>
                    </ul>
                  </Card>
                </>
              ) : (
                <Card className="bg-white border-gray-200 p-12 text-center shadow-sm">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Award className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">No Scholarships Found</h3>
                  <p className="text-gray-500 mb-4 max-w-md mx-auto">
                    Try adjusting your filters. You might qualify for more scholarships by:
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1 text-left max-w-xs mx-auto">
                    <li>• Selecting "Test Optional" if you don't have SAT scores</li>
                    <li>• Checking if your state has specific programs</li>
                    <li>• Adjusting your income estimate</li>
                  </ul>
                </Card>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Scholarships;
